import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Box, Button, FormControlLabel, Grid, Paper, Stack, Switch, TextField } from '@mui/material';

import AppForm from '@/__core/components/AppForm';
import { LabeledField } from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { AddButtonAutocomplete } from '@/__core/hocs';
import { ItemAddOptionModal, ItemSupplierAddModal, ItemUomAddModal } from './modals';

import {
  createItemGroupOption,
  createItemManufacturerOption,
  createItemSupplierOption,
  createItemTypeOption,
  createItemUomOption,
  getItemTypeOptions,
  InventoryItemFormData,
  InventoryItemOption,
} from '../models/ItemModel';

interface ItemFormProps {
  formData: InventoryItemFormData;
  supplierOptions: InventoryItemOption[];
  manufacturerOptions: InventoryItemOption[];
  groupOptions: InventoryItemOption[];
  uomOptions: InventoryItemOption[];
  statusOptions: Array<{ id: string; name: string }>;
  onChangeForm: (updates: Partial<InventoryItemFormData>) => void;
  onSave: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  saving?: boolean;
  deleting?: boolean;
  isDirty?: boolean;
}

type AddOptionCustomDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<any>;
  loading?: boolean;
};

const GroupAddModal: React.FC<AddOptionCustomDialogProps> = (props) => (
  <ItemAddOptionModal {...props} title='Thêm nhóm vật tư' label='Tên nhóm vật tư' placeholder='Nhập tên nhóm vật tư' />
);

const ItemTypeAddModal: React.FC<AddOptionCustomDialogProps> = (props) => (
  <ItemAddOptionModal {...props} title='Thêm loại vật tư' label='Tên loại vật tư' placeholder='Nhập tên loại vật tư' />
);

const ManufacturerAddModal: React.FC<AddOptionCustomDialogProps> = (props) => (
  <ItemAddOptionModal {...props} title='Thêm nhà sản xuất' label='Tên nhà sản xuất' placeholder='Nhập tên nhà sản xuất' />
);

export default function ItemForm({
  formData,
  supplierOptions,
  manufacturerOptions,
  groupOptions,
  uomOptions,
  onChangeForm,
  onSave,
  onCancel = () => {},
  onDelete,
  canEdit = true,
  canDelete = false,
  saving = false,
  deleting = false,
  isDirty = false,
}: ItemFormProps) {
  const readOnly = !canEdit;
  const selectedGroup = groupOptions.find((option) => option.id === formData.group_id) || null;
  const selectedSupplier = supplierOptions.find((option) => option.id === formData.supplier_id) || null;
  const itemTypeOptions = getItemTypeOptions(formData.company_id);
  const selectedItemType = itemTypeOptions.find((option) => option.id === formData.item_type) || null;
  const selectedManufacturer = manufacturerOptions.find((option) => option.id === formData.manufacturer_id) || null;
  const selectedUom = uomOptions.find((option) => option.id === formData.uom_id) || null;

  React.useEffect(() => {
    const updates: Partial<InventoryItemFormData> = {};

    if (!formData.group_id && groupOptions.length > 0) {
      updates.group_id = groupOptions[0].id;
    }

    if (!formData.supplier_id && supplierOptions.length > 0) {
      updates.supplier_id = supplierOptions[0].id;
    }

    if (!formData.manufacturer_id && manufacturerOptions.length > 0) {
      updates.manufacturer_id = manufacturerOptions[0].id;
    }

    if (!formData.uom_id && uomOptions.length > 0) {
      updates.uom_id = uomOptions[0].id;
    }

    if (Object.keys(updates).length > 0) {
      onChangeForm(updates);
    }
  }, [
    formData.group_id,
    formData.supplier_id,
    formData.manufacturer_id,
    formData.uom_id,
    groupOptions,
    supplierOptions,
    manufacturerOptions,
    uomOptions,
    onChangeForm,
  ]);

  const SupplierAddModal: React.FC<AddOptionCustomDialogProps> = (props) => <ItemSupplierAddModal {...props} />;

  const UomAddModal: React.FC<AddOptionCustomDialogProps> = (props) => (
    <ItemUomAddModal
      {...props}
      uomOptions={uomOptions}
      defaultToUomId={formData.uom_id || uomOptions[0]?.id || ''}
    />
  );

  return (
    <AppForm
      onSubmit={onSave}
      onCancel={onCancel}
      submitting={saving}
      submitDisabled={readOnly || saving || deleting || !isDirty}
      submitLabel='Lưu vật tư'
      submittingLabel='Đang lưu...'
      containerSx={{
        m: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Mã vật tư' labelWidth={100}>
              <TextField
                size='small'
                value={formData.code}
                onChange={(event) => onChangeForm({ code: event.target.value })}
                fullWidth
                disabled={readOnly}
                autoComplete='off'
              />
            </LabeledField>

            <LabeledField label='Tên vật tư' labelWidth={100}>
              <TextField
                size='small'
                value={formData.name}
                onChange={(event) => onChangeForm({ name: event.target.value })}
                fullWidth
                disabled={readOnly}
                autoComplete='off'
              />
            </LabeledField>

            <LabeledField label='Nhóm vật tư' labelWidth={100}>
              <AddButtonAutocomplete
                options={groupOptions}
                value={selectedGroup}
                onChange={(_event, newValue) => {
                  const option = newValue as InventoryItemOption | null;
                  onChangeForm({ group_id: option?.id || '' });
                }}
                placeholder='Chọn nhóm vật tư'
                noOptionsText='Không tìm thấy nhóm vật tư'
                CustomDialog={GroupAddModal}
                onAddNewCustom={async (data) => {
                  const created = await createItemGroupOption(data.name, formData.company_id);
                  onChangeForm({ group_id: created.id });
                  return created;
                }}
                disabled={readOnly}
                canAddNew={!readOnly}
                fullWidth
              />
            </LabeledField>

            <LabeledField label='Nhà cung cấp' labelWidth={100}>
              <AddButtonAutocomplete
                options={supplierOptions}
                value={selectedSupplier}
                onChange={(_event, newValue) => {
                  const option = newValue as InventoryItemOption | null;
                  onChangeForm({ supplier_id: option?.id || '' });
                }}
                placeholder='Chọn nhà cung cấp'
                noOptionsText='Không tìm thấy nhà cung cấp'
                CustomDialog={SupplierAddModal}
                onAddNewCustom={async (data) => {
                  const created = await createItemSupplierOption(data.name, formData.company_id, {
                    email: data.email,
                    phone: data.phone,
                    address: data.address,
                  });
                  onChangeForm({ supplier_id: created.id });
                  return created;
                }}
                disabled={readOnly}
                canAddNew={!readOnly}
                fullWidth
              />
            </LabeledField>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Loại vật tư' labelWidth={100}>
              <AddButtonAutocomplete
                options={itemTypeOptions}
                value={selectedItemType}
                onChange={(_event, newValue) => {
                  const option = newValue as InventoryItemOption | null;
                  onChangeForm({ item_type: option?.id || '' });
                }}
                placeholder='Chọn loại vật tư'
                noOptionsText='Không tìm thấy loại vật tư'
                CustomDialog={ItemTypeAddModal}
                onAddNewCustom={async (data) => {
                  const created = await createItemTypeOption(data.name, formData.company_id);
                  onChangeForm({ item_type: created.id });
                  return created;
                }}
                disabled={readOnly}
                canAddNew={!readOnly}
                fullWidth
              />
            </LabeledField>

            <LabeledField label='Nhà sản xuất' labelWidth={100}>
              <AddButtonAutocomplete
                options={manufacturerOptions}
                value={selectedManufacturer}
                onChange={(_event, newValue) => {
                  const option = newValue as InventoryItemOption | null;
                  onChangeForm({ manufacturer_id: option?.id || '' });
                }}
                placeholder='Chọn nhà sản xuất'
                noOptionsText='Không tìm thấy nhà sản xuất'
                CustomDialog={ManufacturerAddModal}
                onAddNewCustom={async (data) => {
                  const created = await createItemManufacturerOption(data.name, formData.company_id);
                  onChangeForm({ manufacturer_id: created.id });
                  return created;
                }}
                disabled={readOnly}
                canAddNew={!readOnly}
                fullWidth
              />
            </LabeledField>

            <LabeledField label='ĐVT' labelWidth={100}>
              <AddButtonAutocomplete
                options={uomOptions}
                value={selectedUom}
                onChange={(_event, newValue) => {
                  const option = newValue as InventoryItemOption | null;
                  onChangeForm({ uom_id: option?.id || '' });
                }}
                placeholder='Chọn ĐVT'
                noOptionsText='Không tìm thấy ĐVT'
                CustomDialog={UomAddModal}
                onAddNewCustom={async (data) => {
                  const created = await createItemUomOption(data.name, formData.company_id, data.conversion);
                  onChangeForm({ uom_id: created.id });
                  return created;
                }}
                disabled={readOnly}
                canAddNew={!readOnly}
                fullWidth
              />
            </LabeledField>

            <LabeledField label='Theo dõi' labelWidth={100}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControlLabel
                  control={<Switch checked={formData.track_lot} onChange={(event) => onChangeForm({ track_lot: event.target.checked })} disabled={readOnly} />}
                  label='Theo dõi lô'
                />
                <FormControlLabel
                  control={<Switch checked={formData.track_location} onChange={(event) => onChangeForm({ track_location: event.target.checked })} disabled={readOnly} />}
                  label='Theo dõi vị trí'
                />
              </Stack>
            </LabeledField>
          </Grid>
        </Grid>

        <Grid container rowSpacing={1} sx={sharedFormStyles.additionalTextGrid}>
          <Grid size={{ xs: 12 }}>
            <LabeledField label='Ghi chú' labelWidth={130} alignItems='start'>
              <TextareaAutosize
                minRows={2}
                value={formData.specification}
                onChange={(event) => onChangeForm({ specification: event.target.value })}
                placeholder='Nhập ghi chú kỹ thuật hoặc mô tả vật tư...'
                disabled={readOnly}
                style={sharedFormStyles.additionalTextarea(canEdit)}
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Paper>
    </AppForm>
  );
}
